import { useMemo } from 'react';
import { aggregateMonthly, netRevenue } from '../../lib/revenue-aggregation.js';
import type { RevenueColumn, RevenueRecord } from '../../types/revenue.js';

interface RevenueMonthlyViewProps {
  columns: RevenueColumn[];
  rows: RevenueRecord[];
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計の月次タブ。月別×売上カテゴリ別テーブルと、純売上の前月比を表示する。
 * @graph-connects aggregateMonthly [calls] 月次集約
 * @graph-connects netRevenue [calls] 純売上算出
 */
export function RevenueMonthlyView({ columns, rows }: RevenueMonthlyViewProps) {
  const salesColumns = columns.filter((c) => c.block === '売上');
  const buckets = useMemo(() => aggregateMonthly(rows), [rows]);

  return (
    <div className="mt-6 overflow-x-auto rounded-lg border border-[var(--color-border)]">
      <table className="min-w-full border-collapse text-right text-sm">
        <thead>
          <tr className="bg-[var(--color-bg-base)]">
            <th className="border-b border-[var(--color-border)] px-3 py-2 text-left font-semibold text-[var(--color-text-secondary)]">
              月
            </th>
            {salesColumns.map((col) => (
              <th
                key={col.key}
                className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]"
              >
                {col.label}
              </th>
            ))}
            <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-semibold whitespace-nowrap text-[var(--color-text-primary)]">
              純売上
            </th>
            <th className="border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]">
              前月比
            </th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket, i) => {
            const prevBucket = i > 0 ? buckets[i - 1] : undefined;
            const prevNet = prevBucket ? netRevenue(prevBucket.record) : null;
            const net = netRevenue(bucket.record);
            const momPct =
              prevNet !== null && prevNet !== 0 ? ((net - prevNet) / prevNet) * 100 : null;
            return (
              <tr key={bucket.month} className="hover:bg-[var(--color-bg-base)]">
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 text-left font-mono text-[var(--color-text-primary)]">
                  {bucket.month}
                </td>
                {salesColumns.map((col) => (
                  <td
                    key={col.key}
                    className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]"
                  >
                    {bucket.record[col.key].toLocaleString('ja-JP')}
                  </td>
                ))}
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 font-semibold tabular-nums text-[var(--color-text-primary)]">
                  {net.toLocaleString('ja-JP')}
                </td>
                <td className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-secondary)]">
                  {momPct === null ? '-' : `${momPct >= 0 ? '+' : ''}${momPct.toFixed(1)}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
